/**
 * api.js — Firebase Callable Functions client
 *
 * Drop-in replacement for the old axios/REST api.js.
 * All exports (authAPI, walletAPI, etc.) keep the same names and signatures
 * so no changes are needed in any page/component that imports from this file.
 *
 * The only exception is authAPI.register / authAPI.login — those are now
 * handled directly in AuthPage.js using Firebase Auth SDK, so they are
 * kept here as no-ops / stubs to avoid import errors if referenced elsewhere.
 */

import { httpsCallable } from 'firebase/functions';
import { functions, auth } from './firebase';

// ─── Helper ────────────────────────────────────────────────────────────────
// Wraps a Firebase callable so it:
//   1. Calls the named Cloud Function with the given data
//   2. Returns { data: result.data } to match the old axios response shape
//      so existing callers (response.data.xxx) continue to work unchanged
// ───────────────────────────────────────────────────────────────────────────

const call = (name) => async (data) => {
  const fn = httpsCallable(functions, name);
  const result = await fn(data ?? null);
  return { data: result.data };
};

// ─── Auth ───────────────────────────────────────────────────────────────────
// Registration and login are handled by Firebase Auth in AuthPage.js.
// These stubs are here only to avoid breaking any file that still imports authAPI.
export const authAPI = {
  register: () => Promise.resolve({ data: {} }),
  login:    () => Promise.resolve({ data: {} }),
};

// ─── Wallet ─────────────────────────────────────────────────────────────────
export const walletAPI = {
  getBalances: call('get_wallet_balances'),
  deposit:     call('deposit_crypto'),
};

// ─── Deposits ────────────────────────────────────────────────────────────────
// NOWPayments removed. Deposits now happen via Dynamic embedded-wallet addresses.
// depositAPI kept as a stub so any remaining imports won't break at runtime.
export const depositAPI = {
  createPayment:  () => Promise.resolve({ data: {} }),
  getStatus:      () => Promise.resolve({ data: {} }),
  getHistory:     () => Promise.resolve({ data: [] }),
  getCurrencies:  () => Promise.resolve({ data: [] }),
};

// ─── Transfer ────────────────────────────────────────────────────────────────
export const transferAPI = {
  transferCrypto: call('transfer_crypto'),
};

// ─── Convert ─────────────────────────────────────────────────────────────────
export const convertAPI = {
  convertToAED: call('convert_crypto_to_aed'),
};

// ─── Cards ───────────────────────────────────────────────────────────────────
export const cardsAPI = {
  getAvailable:      call('get_available_cards'),
  purchase:          call('purchase_card'),
  purchaseWithCrypto: call('purchase_card_with_crypto'),
  getMyCards:        call('get_my_cards'),
  requestPhysical:   call('request_physical_card'),
};

// ─── Bank Transfer ───────────────────────────────────────────────────────────
export const bankAPI = {
  transfer:    call('transfer_to_bank'),
  getUAEBanks: call('get_uae_banks'),
};

// ─── Transactions ────────────────────────────────────────────────────────────
export const transactionsAPI = {
  getHistory: call('get_transaction_history'),
};

// ─── Profile ─────────────────────────────────────────────────────────────────
export const profileAPI = {
  getProfile:    call('get_profile'),
  updateProfile: call('update_profile'),
  uploadPhoto:   call('upload_profile_photo'),
  deletePhoto:   call('delete_profile_photo'),
};

// ─── 401 handler ─────────────────────────────────────────────────────────────
// The old axios interceptor redirected to /auth on 401.
// Firebase handles token expiry automatically, but if a function throws
// UNAUTHENTICATED we catch it globally here.
// Add this once in your app entry point (index.js or App.js) if needed:
//
//   import { getFunctions } from 'firebase/functions';
//   // Firebase callable errors have error.code === 'functions/unauthenticated'

export default {};
